import { useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { NV_PATHS, NvButton, NvEmptyState, NvErrorState, NvFilterChip, NvSkeletonCard } from "@/components/navigator";
import { NvClockIcon, NvMailIcon } from "@/components/navigator/nv-icons-mypage";
import { useRequest } from "@/hooks/use-request";
import { isNotFoundError } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { dateOnly, getMyInquiry, listMyInquiries, type InquiryStatus } from "@/services/navigator/my-page-service";

import { MY_PAGE_BASE, MyPageHeader, useMyPage } from "./my-page-shared";

// 문의 이력 · 상세 (Figma ver.7: 문의 이력 847:47968 · 문의 상세 답변 보기 847:48059 · 답변 준비 중 847:51593, 제안 11 · 04 B안)

// 상태 칩 색 통일 (제안 11): 접수 = 회색, 검토중 = 주황, 답변 완료 = 초록
const STATUS_CHIP: Record<InquiryStatus, string> = {
  new: "bg-[#F3F4F6] text-[#5E626A]",
  in_review: "bg-[#FFF0E8] text-nv-brand",
  resolved: "bg-[#E8F5EE] text-[#1E8449]",
};

// 설계서 slide 108: 5개씩 보여주고 나머지는 페이지네이션
const INQUIRY_PAGE_SIZE = 5;

function Pagination({ page, pages, onChange }: { page: number; pages: number; onChange: (page: number) => void }) {
  return (
    <nav aria-label="페이지" className="flex items-center justify-center gap-2">
      <button type="button" aria-label="이전 페이지" disabled={page <= 1} onClick={() => onChange(page - 1)} className="-mx-1 px-1 text-[14px] leading-[17px] text-nv-text-disabled enabled:text-nv-text-primary">
        ‹
      </button>
      {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
        <button
          key={p}
          type="button"
          aria-current={p === page ? "page" : undefined}
          onClick={() => onChange(p)}
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-nv-chip border text-[14px] leading-[17px]",
            p === page ? "border-nv-brand bg-nv-brand-subtle font-medium text-nv-text-primary" : "border-transparent text-nv-text-secondary hover:text-nv-text-primary",
          )}
        >
          {p}
        </button>
      ))}
      <button type="button" aria-label="다음 페이지" disabled={page >= pages} onClick={() => onChange(page + 1)} className="-mx-1 px-1 text-[14px] leading-[17px] text-nv-text-disabled enabled:text-nv-text-primary">
        ›
      </button>
    </nav>
  );
}

export function MyInquiriesSection() {
  const navigate = useNavigate();
  // 상태 필터는 다중 선택, 아무것도 안 고르면 전체 (설계서 slide 108: 전체를 선택한 채 다른 필터를 고르면 전체가 풀리고, 개별 필터를 모두 풀면 전체)
  const [selected, setSelected] = useState<InquiryStatus[]>([]);
  const [page, setPage] = useState(1);
  const { data, error, loading, reload } = useRequest((signal) => listMyInquiries(undefined, signal), []);
  const chips: { value: InquiryStatus | null; label: string; count: number }[] = data
    ? [
        { value: null, label: "전체", count: data.counts.all },
        { value: "new", label: "접수", count: data.counts.new },
        { value: "in_review", label: "검토중", count: data.counts.in_review },
        { value: "resolved", label: "답변 완료", count: data.counts.resolved },
      ]
    : [];
  const toggle = (value: InquiryStatus | null) => {
    setPage(1);
    if (value === null) return setSelected([]);
    setSelected((cur) => (cur.includes(value) ? cur.filter((s) => s !== value) : [...cur, value]));
  };
  const filtered = data ? (selected.length === 0 ? data.items : data.items.filter((i) => selected.includes(i.status))) : [];
  const pages = Math.max(1, Math.ceil(filtered.length / INQUIRY_PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * INQUIRY_PAGE_SIZE, current * INQUIRY_PAGE_SIZE);
  return (
    <>
      <MyPageHeader title="문의 이력" description={`접수한 문의 ${data?.counts.all ?? 0}건`} />
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <NvFilterChip key={c.label} selected={c.value === null ? selected.length === 0 : selected.includes(c.value)} onClick={() => toggle(c.value)}>
            {c.label} {c.count}
          </NvFilterChip>
        ))}
      </div>
      {!!error && <NvErrorState error={error} onRetry={reload} />}
      {loading && !data && [0, 1, 2].map((i) => <NvSkeletonCard key={i} />)}
      {!error && data && filtered.length === 0 && (
        <NvEmptyState
          action={
            <NvButton variant="secondary" asChild>
              <Link to={NV_PATHS.contact}>문의하기</Link>
            </NvButton>
          }
        >
          접수한 문의가 없어요.
        </NvEmptyState>
      )}
      {!error &&
        visible.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => navigate(`${MY_PAGE_BASE}/inquiries/${item.id}`)}
            className="flex min-h-[142px] items-center justify-between gap-5 rounded-nv-card border border-nv-border bg-nv-surface px-6 py-5 text-left hover:border-nv-border-strong"
          >
            <span className="flex min-w-0 flex-1 flex-col gap-2">
              <span className={cn("inline-flex h-[21px] w-fit items-center rounded-nv-pill px-[10px] text-[11px] font-bold leading-[13px]", STATUS_CHIP[item.status])}>{item.status_label}</span>
              <span className="truncate text-[16px] font-medium leading-[19px] text-nv-text-primary">{item.title}</span>
              <span className="block h-5 max-w-[760px] truncate text-[13px] leading-4 text-[#6A6F77]">{item.preview ?? ""}</span>
              <span className="whitespace-pre text-[13px] leading-4 text-nv-text-secondary">{`대상 에이전트 ${item.target ?? "-"}   ·   접수일 ${dateOnly(item.created_at)}`}</span>
            </span>
            <span className="flex shrink-0 items-center gap-3">
              {item.replied_at && (
                <span className="inline-flex h-[21px] items-center rounded-nv-pill bg-nv-bg-subtle px-[10px] text-[11px] font-medium leading-[13px] text-nv-success">
                  답변 도착 · {dateOnly(item.replied_at)}
                </span>
              )}
              <span aria-hidden className="text-[22px] leading-[26px] text-nv-text-secondary">
                ›
              </span>
            </span>
          </button>
        ))}
      {!error && data && filtered.length > 0 && <Pagination page={current} pages={pages} onChange={setPage} />}
    </>
  );
}

// "PwC AX Advisory 팀이" — 받침에 따라 이 · 가
function withSubject(name: string) {
  const code = name.trim().charCodeAt(name.trim().length - 1) - 0xac00;
  return `${name}${code >= 0 && code < 11172 && code % 28 === 0 ? "가" : "이"}`;
}

function StepLabels({ steps }: { steps: { label: string; at: string | null; done: boolean }[] }) {
  return (
    <ol className="grid grid-cols-3 gap-[6px]">
      {steps.map((step, i) => (
        <li key={step.label} className="flex items-baseline gap-2">
          <span className={cn("text-[14px] leading-[17px]", step.done ? "font-bold text-nv-text-primary" : "font-medium text-nv-text-disabled")}>
            {i + 1}. {step.label}
          </span>
          <span className={cn("text-[13px] leading-4", step.done ? "text-[#6A6F77]" : "text-nv-text-disabled")}>{step.done ? dateOnly(step.at) : "-"}</span>
        </li>
      ))}
    </ol>
  );
}

function Panel({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("flex flex-col gap-3 rounded-nv-card border border-nv-border bg-nv-surface px-7 py-6", className)}>{children}</div>;
}

export function MyInquiryDetailSection() {
  const { id = "" } = useParams();
  const { summary } = useMyPage();
  const { data: detail, error, reload } = useRequest((signal) => getMyInquiry(id, signal), [id]);
  // Only an unknown / invalid id is "not found"; a failed load (5xx, network) gets [다시 시도]
  const missing = !!error && isNotFoundError(error);
  const back = (
    <Link to={`${MY_PAGE_BASE}/inquiries`} className="w-fit text-[13px] leading-4 text-nv-text-secondary hover:text-nv-text-primary">
      ‹&nbsp;&nbsp;문의 이력으로
    </Link>
  );
  if (missing) return <>{back}<NvEmptyState>문의를 찾을 수 없어요.</NvEmptyState></>;
  if (error) return <>{back}<NvErrorState error={error} onRetry={reload} /></>;
  if (!detail) return back;
  const target = detail.attachments.agent_name ?? detail.attachments.case_title ?? "-";
  const attach = [
    detail.attachments.agent_name && `대상 에이전트 ${detail.attachments.agent_name}`,
    detail.attachments.supplier_name && `공급사 ${detail.attachments.supplier_name}`,
    detail.attachments.case_title && `Case Study ${detail.attachments.case_title}`,
    detail.attachments.source_label && `진입 출처 ${detail.attachments.source_label}`,
  ].filter(Boolean) as string[];
  const answered = detail.replies.length > 0;
  const answeredAt = detail.steps[detail.steps.length - 1]?.at ?? detail.replies[detail.replies.length - 1]?.created_at ?? null;
  // 진행 상태 문구: 답변 등록 · 검토 중만 정의돼 있어요 (접수 상태 문구는 설계서에 없음 → 칩만)
  const statusLine = answered ? `답변이 등록되었어요 · ${dateOnly(answeredAt)}` : detail.status === "in_review" ? "담당 팀이 문의 내용을 검토하고 있어요" : null;
  return (
    <>
      {back}
      <div className="flex flex-col gap-[18px] rounded-nv-card border border-nv-border bg-nv-surface p-7">
        <h1 className="text-[22px] font-medium leading-[26px] text-nv-text-primary">{detail.title}</h1>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-[10px]">
            <span className={cn("inline-flex h-6 items-center rounded-nv-pill px-[10px] text-[13px] font-bold leading-4", STATUS_CHIP[detail.status])}>{detail.status_label}</span>
            {statusLine && <span className="text-[14px] font-medium leading-[17px] text-nv-text-primary">{statusLine}</span>}
          </div>
          <div aria-hidden className="grid grid-cols-3 gap-[6px]">
            {detail.steps.map((step) => (
              <span key={step.label} className={cn("h-2 rounded-[4px]", step.done ? "bg-nv-brand" : "bg-nv-bg-muted")} />
            ))}
          </div>
          <StepLabels steps={detail.steps} />
        </div>
        <dl className="grid grid-cols-3 items-center rounded-nv-card bg-nv-bg-page px-[18px] py-[14px]">
          {[
            ["대상 에이전트", target],
            ["접수일", dateOnly(detail.created_at)],
            ["담당 팀", detail.team_name],
          ].map(([label, value]) => (
            <div key={label} className="flex flex-col gap-[3px]">
              <dt className="text-[12px] leading-[14px] text-nv-text-secondary">{label}</dt>
              <dd className="text-[14px] font-medium leading-[17px] text-nv-text-primary">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="flex flex-col gap-4">
        <Panel>
          <div className="flex items-center gap-2">
            <span className="text-[16px] font-bold leading-[19px] text-nv-text-primary">내 문의</span>
            <span className="text-[13px] leading-4 text-[#8E939A]">{dateOnly(detail.created_at)} 접수</span>
          </div>
          <p className="whitespace-pre-line text-[15px] leading-6 text-nv-text-primary">{detail.content}</p>
          {attach.length > 0 && (
            <div className="flex flex-wrap gap-[6px]">
              {attach.map((text) => (
                <span key={text} className="inline-flex h-[22px] items-center rounded-nv-pill bg-nv-bg-subtle px-[10px] text-[12px] leading-[14px] text-nv-text-secondary">
                  {text}
                </span>
              ))}
            </div>
          )}
        </Panel>
        {answered ? (
          detail.replies.map((reply, i) => (
            // 답변은 왼쪽 주황 강조선 (4)
            <div key={i} className="relative flex flex-col gap-3 overflow-hidden rounded-nv-card bg-nv-surface px-[29px] py-[25px] shadow-[inset_0_0_0_1px_#E6E6E3]">
              <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-nv-brand" />
              <div className="flex items-center gap-2">
                <span className="text-[16px] font-bold leading-[19px] text-nv-text-primary">PwC 답변</span>
                <span className="inline-flex h-5 items-center rounded-nv-pill bg-[#E8F5EE] px-2 text-[12px] font-bold leading-[14px] text-[#1E8449]">답변 완료</span>
                <span className="text-[13px] leading-4 text-[#8E939A]">
                  {reply.team} · {dateOnly(reply.created_at)}
                </span>
              </div>
              <p className="whitespace-pre-line text-[15px] leading-6 text-nv-text-primary">{reply.body}</p>
              <p className="text-[13px] leading-4 text-[#8E939A]">추가 문의는 Contact Us에서 새로 작성해 주세요.</p>
            </div>
          ))
        ) : (
          // 답변 미등록 (접수 · 검토중): 점선 빈 상태 카드
          <div className="flex flex-col items-center gap-[10px] rounded-nv-card border border-dashed border-nv-border-strong bg-[#FAFAFA] px-7 py-9 text-center">
            <span aria-hidden className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF0E8] text-nv-brand">
              <NvClockIcon />
            </span>
            <p className="text-[17px] font-bold leading-5 text-nv-text-primary">답변을 준비하고 있어요</p>
            <p className="text-[14px] leading-[22px] text-[#6A6F77]">{withSubject(detail.team_name)} 문의 내용을 검토하고 있어요. 답변이 등록되면 가입하신 이메일로 알려드릴게요.</p>
            <span className="inline-flex h-7 items-center gap-[6px] rounded-full border border-nv-border bg-nv-surface px-3 text-[12px] font-medium leading-[14px] text-[#6A6F77]">
              <NvMailIcon />
              {summary.profile.email} 로 알림
            </span>
          </div>
        )}
      </div>
    </>
  );
}
